import { useState } from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { getPostAuthRedirect } from '../utils/authNavigation'
import './SignupForm.css'

function SignupForm({ onSwitchToLogin }) {
  const { signup } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [policyAgreed, setPolicyAgreed] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const getPasswordStrength = (pwd) => {
    if (pwd.length === 0) return { strength: 0, label: '' }
    if (pwd.length < 8) return { strength: 1, label: 'Too short' }
    
    let strength = 0
    if (/[a-z]/.test(pwd)) strength++
    if (/[A-Z]/.test(pwd)) strength++
    if (/[0-9]/.test(pwd)) strength++
    if (/[^a-zA-Z0-9]/.test(pwd)) strength++

    const labels = {
      1: 'Weak',
      2: 'Fair',
      3: 'Good',
      4: 'Strong'
    }

    return { strength, label: labels[strength] || 'Weak' }
  }

  const passwordStrength = getPasswordStrength(password)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Client-side validation
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    if (!policyAgreed) {
      setError('You must agree to the site user policy and legal terms to create an account')
      return
    }

    setIsLoading(true)

    try {
      const result = await signup(email, username, password, policyAgreed)
      if (!result.success) {
        setError(result.error || 'Signup failed')
      } else {
        // If successful, redirect to intended destination or dashboard
        navigate(getPostAuthRedirect(location), { replace: true })
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      {error && (
        <div className="auth-error">
          {error}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="signup-email">Email</label>
        <input
          type="email"
          id="signup-email"
          name="email"
          placeholder="your@email.com"
          className="form-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label htmlFor="signup-username">Username</label>
        <input
          type="text"
          id="signup-username"
          name="username"
          placeholder="username"
          className="form-input"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          minLength={3}
          maxLength={30}
          pattern="[a-zA-Z0-9_-]+"
          disabled={isLoading}
        />
        <small className="form-hint">3-30 characters, letters, numbers, underscores, and hyphens only</small>
      </div>

      <div className="form-group">
        <label htmlFor="signup-password">Password</label>
        <div className="password-input-wrap">
          <input
            type={showPassword ? 'text' : 'password'}
            id="signup-password"
            name="password"
            placeholder="••••••••"
            className="form-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            disabled={isLoading}
          />
          <button
            type="button"
            className="password-toggle-btn"
            onClick={() => setShowPassword((prev) => !prev)}
            disabled={isLoading}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        {password && (
          <div className="password-strength">
            <div className="password-strength-bar">
              <div
                className={`password-strength-fill strength-${passwordStrength.strength}`}
                style={{ width: `${(passwordStrength.strength / 4) * 100}%` }}
              />
            </div>
            <span className="password-strength-label">{passwordStrength.label}</span>
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="signup-confirm-password">Confirm Password</label>
        <div className="password-input-wrap">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            id="signup-confirm-password"
            name="confirmPassword"
            placeholder="••••••••"
            className="form-input"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            className="password-toggle-btn"
            onClick={() => setShowConfirmPassword((prev) => !prev)}
            disabled={isLoading}
            aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
          >
            {showConfirmPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        {confirmPassword && password !== confirmPassword && (
          <small className="form-error-hint">Passwords do not match</small>
        )}
      </div>

      <div className="form-group" style={{ marginTop: '0.5rem' }}>
        <label
          htmlFor="signup-policy"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5rem',
            fontSize: '0.9rem',
            lineHeight: 1.5,
            cursor: 'pointer',
            color: 'var(--text-secondary)',
          }}
        >
          <input
            type="checkbox"
            id="signup-policy"
            checked={policyAgreed}
            onChange={(e) => setPolicyAgreed(e.target.checked)}
            disabled={isLoading}
            style={{ marginTop: '0.25rem' }}
          />
          <span>
            I agree to the site&apos;s{' '}
            <Link
              to="/docs#legal"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--accent)', textDecoration: 'underline' }}
            >
              user policy and legal terms
            </Link>
            , including content rules, moderation expectations, and DMCA/copyright policies.
          </span>
        </label>
      </div>

      <button
        type="submit"
        className="auth-submit-btn"
        disabled={isLoading || password !== confirmPassword || !policyAgreed}
      >
        {isLoading ? 'Creating Account...' : 'Create Account'}
      </button>

      <div className="auth-divider">
        <span>or</span>
      </div>

      <button
        type="button"
        className="auth-secondary-btn"
        onClick={onSwitchToLogin}
        disabled={isLoading}
      >
        Already have an account? Sign In
      </button>
    </form>
  )
}

export default SignupForm
